import {Box, Button, Divider, Stack, Typography, useTheme} from '@mui/material';
import React from 'react';

import {StyledCard} from 'presentation/components';
import Icon from 'presentation/icons';
import {translations} from 'presentation/translations-new';

import {formatPrice, formatText} from 'presentation/utils';
import {convertBRLCurrencyToNumber} from 'presentation/utils/convertBRLCurrencyToNumber';
import {createStyles} from './styles';
import {TherapyCardProps} from './types';

const {details} = translations['pt-br'].commons;
const {
	session: sessionLabel,
	sessions: sessionsLabel,
	totalSessions,
} = translations['pt-br'].privateFlow.refund.therapies;

export const TherapyCard = ({data, onDetailsClick}: TherapyCardProps) => {
	const theme = useTheme();
	const styles = createStyles(theme);
	const {therapyType, sessions} = data;

	const handleDetailsClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		onDetailsClick(data);
	};

	const sessionsText = `${sessions.length === 1 ? sessionLabel.toLowerCase() : sessionsLabel.toLowerCase()}`;
	const sessionsQuantity = `${sessions.length.toString().padStart(2, '0')}`;
	const sessionsQuantityWithText = `${sessionsQuantity} ${sessionsText}`;

	const totalValue = sessions
		.map(session => convertBRLCurrencyToNumber(session?.sessionValue ?? ''))
		.reduce((acc, sessionValue) => acc + sessionValue, 0);

	return (
		<StyledCard.Container
			$borderTop
			$borderColor={theme.palette.primary.main}
			sx={styles.cardContainer}>
			<Stack direction="column" spacing={1}>
				<Stack direction="row" justifyContent="space-between" alignItems="center">
					<Typography
						variant="h6"
						color={theme.palette.primary.dark}
						sx={styles.therapyTitle}>
						{formatText(therapyType.description)}
					</Typography>
					<Typography variant="body1" sx={styles.sessionsText}>
						{sessionsQuantityWithText}
					</Typography>
				</Stack>
			</Stack>

			<Divider />

			<Stack direction="column" spacing={2} flex={1}>
				<Stack direction="row" spacing={1} alignItems="flex-start">
					<Box sx={styles.iconBox}>
						<Icon
							name="attach-money-filled"
							size={20}
							color={theme.palette.primary.main}
						/>
					</Box>
					<Stack direction="column" spacing={0.5}>
						<Typography variant="caption" sx={styles.sectionLabel}>
							{totalSessions}
						</Typography>
						<Typography variant="body1" sx={styles.sectionValue}>
							{formatPrice(totalValue)}
						</Typography>
					</Stack>
				</Stack>
			</Stack>

			<Stack
				direction="row"
				justifyContent="flex-end"
				alignItems="center"
				sx={styles.detailsButtonContainer}>
				<Button
					variant="text"
					onClick={handleDetailsClick}
					endIcon={
						<Icon
							name="chevron-right-outlined"
							color={theme.palette.primary.main}
							size={16}
						/>
					}
					sx={styles.detailsButton}>
					{details}
				</Button>
			</Stack>
		</StyledCard.Container>
	);
};
